import { Component, OnInit, ViewChild, OnDestroy, ChangeDetectionStrategy, signal } from '@angular/core';

import { MonitorsTableComponent } from '/app/monitors/components/monitors-table/monitors-table.component';
import { MonitorsService } from '/app/monitors/services/monitors.service';
import { Subscription } from 'rxjs';
import { Monitor } from '/app/monitors/models/monitor.model';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { AlarmsTableComponent } from '/app/alarms/components/alarms-table/alarms-table.component';
import { AlarmsDetailsComponent } from '/app/alarms/components/alarms-details/alarms-details.component';
import { Alarm } from '/app/alarms/models/alarm.model';
import { MonitorsDetailsComponent } from '/app/monitors/components/monitors-details/monitors-details.component';
import { UsersService } from '/app/services/users/users.service';
import { HelpService } from '/app/services/help/help.service';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { AlertComponent } from '/app/components/alert/alert.component';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '/app/components/confirm/confirm-dialog/confirm-dialog.component';
import { UsersDetailsComponent } from '/app/users/components/users-details/users-details.component';
import { ADMINISTRATORS, SITRACK } from '/app/constants/auth.constants';
import { DashboardComponent } from '/app/monitors/components/dashboard/dashboard.component';
import { HasRoleDirective } from '/app/shared/directives/has-role.directive';

@Component({
  selector: 'app-monitores',
  imports: [
    MonitorsTableComponent,
    AlarmsTableComponent,
    MatIconModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatTooltipModule,
    MatCardModule,
    AlarmsDetailsComponent,
    MonitorsDetailsComponent,
    AlertComponent,
    UsersDetailsComponent,
    DashboardComponent,
    RouterLink,
    HasRoleDirective
],
  templateUrl: './monitores.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./monitores.component.scss'],
})
export class MonitoresComponent implements OnInit, OnDestroy {
  showAlert = signal(false);
  alertType = signal<'success' | 'error' | 'warning' | 'info'>('info');
  alertMessage = signal('');
  dataSource = signal<Monitor[]>([]);
  isLoading = signal(false);
  error = signal<string | null>(null);
  selectedMonitorId = signal<string>("");
  currentMonitorId = signal<string>("");
  currentMonitorName = signal<string>("");
  selectedAlarm = signal<any>(null);
  showAlarmDetails = signal(false);
  showMonitorDetails = signal(false);
  showUsersDetails = signal(false);
  alarmsFromChild = signal<Alarm[]>([]);
  monitorsFromChild: Monitor[] = [];

  isAdministrator = signal(false);

  isSitrackAdministrator = signal(false);

  userLoginName = signal('');

  @ViewChild(AlarmsTableComponent) alarmsTableComponent?: AlarmsTableComponent;

  private monitorUpdatedSub?: Subscription;

  constructor(
    private monitorsService: MonitorsService,
    private authService: UsersService,
    private helpService: HelpService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    // dataUser=this.authService.getUserInfo()
    const userInfo = this.authService.getUserInfo();

    if (userInfo) {
      this.userLoginName.set(this.decodeUTF8(userInfo.fullName));

      this.isAdministrator.set(userInfo.groups.some((group: string) =>
        ADMINISTRATORS.includes(group)
      ));

      this.isSitrackAdministrator.set(userInfo.groups.some((group: string) =>
        SITRACK.includes(group)
      ));

    }

    // Restaurar preferencia de mostrar/ocultar detalles de alarma
    try {
      const saved = localStorage.getItem('rs_showAlarmDetails');
      if (saved !== null) {
        this.showAlarmDetails.set(JSON.parse(saved));
      }
    } catch (e) {
      // ignore
    }

    // Cargar monitores después de que los demás componentes se inicialicen
    setTimeout(() => {
      this.loadMonitors();
    }, 100);

    // Suscribirse a actualizaciones de monitores para refrescar dataSource in-place
    this.monitorUpdatedSub = this.monitorsService.monitorUpdated.subscribe((m: any) => {
      if (!m) return;
      try {
        this.dataSource.update(items => {
          const idx = items.findIndex(d => String(d.id) === String(m.id));
          if (idx >= 0) {
            const updated = [...items];
            updated[idx] = m;
            return updated;
          }
          return [m, ...items];
        });

        if (this.currentMonitorId() && String(this.currentMonitorId()) === String(m.id)) {
          this.currentMonitorName.set(m.name);
        }
      } catch (e) {
        // ignore
      }
    });

    // Leer parámetro de ruta si existe
    this.activatedRoute.params.subscribe(params => {
      if (params['monitorId']) {
        this.selectedMonitorId.set(params['monitorId']);
        this.currentMonitorId.set(params['monitorId']);
        setTimeout(() => {
          const monitor = this.dataSource().find(m => m.id === params['monitorId']);
          if (monitor) {
            this.currentMonitorName.set(monitor.name);
          }
        }, 500); // Pequeño delay para asegurar que dataSource esté cargado
      }
    });
  }

  private decodeUTF8(str: string): string {
    try {
      return decodeURIComponent(escape(str));
    } catch (e) {
      return str; // Si falla, devolver original
    }
  }

  // Guarda la preferencia de mostrar/ocultar detalles de alarma
  setShowAlarmDetails(show: boolean): void {
    this.showAlarmDetails.set(show);
    try {
      localStorage.setItem('rs_showAlarmDetails', JSON.stringify(show));
    } catch (e) {
      // ignore
    }
  }

  loadMonitors(): void {
    this.isLoading.set(true);
    this.error.set(null);
    this.monitorsService.getMonitors().subscribe({
      next: (data: any) => {
        this.dataSource.set(data as Monitor[]);
        this.isLoading.set(false);
      },
      error: (err: any) => {
        this.error.set('Error al cargar los monitores: ' + (err?.message || String(err)));
        this.isLoading.set(false);
      },
    });
  }

  onMonitorSelected(monitor: { id: string; name: string } | null): void {
    if (monitor) {
      this.selectedMonitorId.set(monitor.id);
      this.currentMonitorId.set(monitor.id);
      this.currentMonitorName.set(monitor.name);
      this.showAlarmDetails.set(false);
      this.showMonitorDetails.set(false);
      this.router.navigate(['/monitors', monitor.id]);
    } else {
      this.selectedMonitorId.set("");
      this.currentMonitorId.set("");
      this.currentMonitorName.set("");
      this.router.navigate(['/monitors']);
    }
  }

  onAlarmSelected(alarm: any): void {
    this.selectedAlarm.set(alarm);
    this.showMonitorDetails.set(false);
    this.showUsersDetails.set(false);
    this.setShowAlarmDetails(true);
  }

  // Handler para clicks en filas que NO deben abrir el panel de detalles automáticamente
  onAlarmClicked(alarm: any): void {
    this.selectedAlarm.set(alarm);
    this.showMonitorDetails.set(false);
    this.showUsersDetails.set(false);
    this.setShowAlarmDetails(true);
  }

  onAlarmsUpdate(alarms: Alarm[]) {
    this.alarmsFromChild.set(alarms);
  }

  openMonitorConfig() {
    this.setShowAlarmDetails(false);
    this.showUsersDetails.set(false);
    this.showMonitorDetails.set(true);
    // Switch to Alarmas tab if Mapa de Riesgo is open
    if (this.alarmsTableComponent) {
      this.alarmsTableComponent.switchToAlarmsTab();
    }
  }


  openUsersConfig() {
    this.setShowAlarmDetails(false);
    this.showMonitorDetails.set(false);
    this.showUsersDetails.set(true);
    // Switch to Alarmas tab if Mapa de Riesgo is open
    if (this.alarmsTableComponent) {
      this.alarmsTableComponent.switchToAlarmsTab();
    }
  }

  logout() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Salida del Sistema',
        message: '¿Estás seguro de salir del sistema?',
        confirmText: 'Sí, Salir',
        cancelText: 'Cancelar',
        icon: 'exit',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.authService.logout();
        this.router.navigate(['/']).then();
      }
    });
  }

  showSuccess(message: string) {
    this.alertType.set('success');
    this.alertMessage.set(message);
    this.showAlert.set(true);
    setTimeout(() => this.showAlert.set(false), 5000);
  }

  showError(message: string) {
    this.alertType.set('error');
    this.alertMessage.set(message);
    this.showAlert.set(true);
    setTimeout(() => this.showAlert.set(false), 5000);
  }

  ayuda() {
    // Determinar qué ruta estamos visitando y mostrar el tour correspondiente
    this.activatedRoute.url.subscribe(url => {
      if (url.length > 0 && url[0].path === 'users') {
        // En la ruta /users - mostrar gestión de usuarios
        this.helpService.startTour(this.helpService.getUsersManagementHelpSteps(), 'Gestión de Usuarios');
      } else if (url.length > 0 && url[0].path === 'monitors' && url.length > 1) {
        // En la ruta /monitors/:monitorId - mostrar detalles de monitores
        this.helpService.startTour([
          ...this.helpService.getMonitoresHelpSteps(),
          ...this.helpService.getAlarmsTableHelpSteps(),
          ...this.helpService.getAlarmDetailsHelpSteps(),
        ], 'Sistema de Monitoreo de Alarmas');
      } else {
        // En la ruta /monitors - mostrar ayuda general
        this.helpService.startTour(this.helpService.getMonitoresHelpSteps(), 'Bienvenido al Sistema');
      }
    });
  }

  ngOnDestroy(): void {
    if (this.monitorUpdatedSub) {
      this.monitorUpdatedSub.unsubscribe();
    }
  }


}
